import type { Check, CheckStatus, Outcome } from './api/types';

/**
 * The ONLY gate for generating the status report. The check must be finished,
 * have found a record, and the portal's File Status must be exactly "ACTIVE"
 * (case/whitespace-insensitive). Anything else, including a blank or unknown
 * status, is refused.
 */
export function isVisaActive(check: Pick<Check, 'status' | 'outcome' | 'is_active' | 'result'> | null | undefined): boolean {
  if (!check || check.status !== 'completed' || check.outcome !== 'found') return false;
  if (check.is_active !== true) return false;
  return (check.result?.file_status ?? '').trim().toUpperCase() === 'ACTIVE';
}

export function isInProgress(status: CheckStatus): boolean {
  return status === 'queued' || status === 'running';
}

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface CheckSummary {
  title: string;
  description: string;
  tone: Tone;
}

/** Plain-language headline for a check, used on the cards and the detail page. */
export function summarizeCheck(check: Check): CheckSummary {
  if (check.status === 'queued') {
    return check.attempts > 0
      ? { title: 'Retrying shortly', description: `The portal didn't answer on attempt ${check.attempts}. Trying again automatically.`, tone: 'info' }
      : { title: 'In the queue', description: 'Your check is waiting to be picked up.', tone: 'info' };
  }
  if (check.status === 'running') {
    return { title: 'Checking the ICP portal', description: 'Looking up your file now. This usually takes 1–5 minutes.', tone: 'info' };
  }
  if (check.status === 'cancelled') {
    return {
      title: 'Check stopped',
      description: `${check.message === 'Cancelled by an admin' ? 'An admin stopped this check' : 'This check was stopped before it finished'}. Any coins it cost were refunded. You can run it again.`,
      tone: 'neutral',
    };
  }
  if (check.status === 'failed') {
    if (check.screenshot_status === 'pending') {
      return { title: 'Screenshot under review', description: 'An admin is reading the result from your portal screenshot. This page updates when they finish.', tone: 'info' };
    }
    return { title: "Couldn't get an answer", description: `${FAILED_REASON[check.outcome ?? 'error'] ?? 'The check did not finish. Please try again.'} Your coins were refunded.`, tone: 'danger' };
  }
  const source = check.result_source === 'screenshot' ? ' (read from your portal screenshot and verified by an admin)' : '';
  switch (check.outcome) {
    case 'found':
      return isVisaActive(check)
        ? { title: 'Visa is ACTIVE', description: `The ICP portal reports this file as active${source}. Your status report is ready.`, tone: 'success' }
        : { title: `File status: ${check.result?.file_status || 'unknown'}`, description: `A record was found${source}, but it is not active, so no report is generated.`, tone: 'warning' };
    case 'no_records':
      return { title: 'No record found', description: 'The portal has no file for these passport details. Double-check the passport number, expiry date and nationality.', tone: 'warning' };
    case 'portal_message':
      return {
        title: "The ICP portal didn't accept the search",
        description: `${check.message ? `The portal said: "${check.message}". ` : ''}Check the passport number, expiry date and nationality, then try again.`,
        tone: 'warning',
      };
    default:
      return { title: 'Finished', description: '', tone: 'neutral' };
  }
}

const FAILED_REASON: Partial<Record<Outcome, string>> = {
  turnstile_failed: "The ICP portal's security check didn't pass, so we couldn't search. Please try again in a few minutes.",
  no_response: "The ICP portal didn't respond. It may be busy; please try again in a few minutes.",
  error:
    "We couldn't finish the search on the ICP portal. This often happens when the portal rejects the passport details, so check the passport number, expiry date and nationality, then try again.",
};

export const OUTCOME_LABEL: Record<Outcome, string> = {
  found: 'Found',
  no_records: 'No record',
  portal_message: 'Portal message',
  turnstile_failed: 'Security check failed',
  no_response: 'No response',
  error: 'Error',
};

/** Badge for a check in lists: combines status, outcome and activity. */
export function checkBadge(check: Check): { label: string; tone: Tone } {
  if (check.status === 'queued') return { label: 'Queued', tone: 'info' };
  if (check.status === 'running') return { label: 'Checking', tone: 'info' };
  if (check.status === 'cancelled') return { label: 'Cancelled', tone: 'neutral' };
  if (check.status === 'failed') {
    return check.screenshot_status === 'pending' ? { label: 'In review', tone: 'info' } : { label: 'Failed', tone: 'danger' };
  }
  if (isVisaActive(check)) return { label: 'Active', tone: 'success' };
  if (check.outcome === 'found') return { label: check.result?.file_status || 'Not active', tone: 'warning' };
  return { label: check.outcome ? OUTCOME_LABEL[check.outcome] : 'Done', tone: 'warning' };
}

/** A failed check whose owner can (still) send a portal screenshot. */
/** A failed or cancelled check can be run again. */
export function canRetry(status: CheckStatus): boolean {
  return status === 'failed' || status === 'cancelled';
}

export function canSendScreenshot(check: Pick<Check, 'status' | 'screenshot_status'>): boolean {
  return check.status === 'failed' && check.screenshot_status !== 'pending';
}
